// client/src/pages/settings/SystemSettings.jsx
// 系统设置: 仅超级管理员(代理/VIP的贴牌入口在侧边栏左下角"页面风格" -> /brand)
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import {
  FiSettings,
  FiSave,
  FiShield,
  FiToggleLeft,
  FiToggleRight,
  FiType
} from 'react-icons/fi';

import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';
import ColorPicker from '../../components/settings/ColorPicker';
import { useAuth } from '../../context/AuthContext';

// 获取系统设置
const fetchSettings = async () => {
  const response = await axios.get('/api/settings');
  return response.data.settings;
};

// 更新系统设置
const updateSettings = async (data) => {
  const response = await axios.put('/api/settings', data);
  return response.data;
};

const SystemSettings = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const isSuper = currentUser?.role === 'admin';

  // 查询系统设置
  const { data: settings, isLoading, error } = useQuery('settings', fetchSettings);

  // 表单状态(平台级, 仅超管可改)
  const [allowRegistration, setAllowRegistration] = useState(true);
  const [systemTitle, setSystemTitle] = useState('运维总控');
  const [systemLogo, setSystemLogo] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState('#4F46E5');
  const [logoPreview, setLogoPreview] = useState('');

  // 平台级设置突变(超管全量保存)
  const updateMutation = useMutation(updateSettings, {
    onSuccess: () => {
      toast.success('系统设置更新成功');
      queryClient.invalidateQueries('settings');

      // 更新全局样式
      document.documentElement.style.setProperty('--color-primary', primaryColor);
      document.documentElement.style.setProperty('--color-secondary', secondaryColor);

      // 更新标题
      document.title = systemTitle;
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || '更新系统设置失败');
    }
  });

  // 当设置数据加载时，初始化平台级表单
  React.useEffect(() => {
    if (settings) {
      setAllowRegistration(settings.allowRegistration);
      setSystemTitle(settings.systemTitle || '运维总控');
      setSystemLogo(settings.systemLogo || '');
      setPrimaryColor(settings.primaryColor || '#3B82F6');
      setSecondaryColor(settings.secondaryColor || '#4F46E5');

      if (settings.systemLogo) {
        setLogoPreview(settings.systemLogo);
      }
    }
  }, [settings]);

  // 处理表单提交(平台级, 超管)
  const handleSubmit = (e) => {
    e.preventDefault();

    const updatedSettings = {
      allowRegistration,
      systemTitle,
      systemLogo,
      primaryColor,
      secondaryColor
    };

    updateMutation.mutate(updatedSettings);
  };

  // 处理Logo上传(超管)
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 检查文件大小和类型
    if (file.size > 1024 * 1024) { // 1MB
      toast.error('图片大小不能超过1MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('请上传图片文件');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Data = e.target.result;
      setSystemLogo(base64Data);
      setLogoPreview(base64Data);
    };
    reader.readAsDataURL(file);
  };

  // 页面动画
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader size={60} />
      </div>
    );
  }

  // 错误状态
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="text-red-500 text-xl mb-4">获取系统设置失败</div>
        <Button onClick={() => queryClient.invalidateQueries('settings')}>重试</Button>
      </div>
    );
  }

  // 非超管(路由层应已拦, 双保险): 无权提示
  if (!isSuper) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="text-yellow-500 text-xl mb-4">无权访问</div>
        <p className="text-[#656d76]">只有超级管理员可以访问此页面</p>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="h-full"
    >
      {/* 页面标题 */}
      <motion.div variants={itemVariants} className="mb-6">
        <h1 className="text-2xl font-bold text-[#1f2328] flex items-center gap-2">
          <FiSettings className="mr-2" /> 系统设置
        </h1>
        <p className="text-[#656d76] mt-1">
          配置全局系统参数和外观(代理/用户的贴牌入口在其侧边栏左下角"页面风格")
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 基本设置 */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-md shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-[#1f2328] mb-6 flex items-center">
            <FiSettings className="mr-2" /> 基本设置
          </h2>

          <div className="space-y-6">
            {/* 系统标题(平台级): 未设个人贴牌的用户显示它 */}
            <div>
              <label className="block text-[#57606a] mb-2">系统标题</label>
              <input
                type="text"
                value={systemTitle}
                onChange={(e) => setSystemTitle(e.target.value)}
                placeholder="输入系统标题"
                className="w-full bg-[#f6f8fa] rounded-md py-3 px-4 text-[#1f2328] border border-[#d0d7de] focus:outline-none brand-ring"
              />
              <p className="text-sm text-[#8b949e] mt-2">
                显示在浏览器标签页和系统顶部的标题。用户可在其贴牌设置中覆盖为自己的标题
              </p>
            </div>

            {/* 系统Logo */}
            <div>
              <label className="block text-[#57606a] mb-2">系统Logo</label>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-md bg-[#f6f8fa] border border-[#d0d7de] flex items-center justify-center overflow-hidden shrink-0">
                  {logoPreview ? (
                    <img src={logoPreview} alt="logo" className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-[#8b949e] text-xs">未设置</span>
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="hidden"
                    id="logo-upload"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => document.getElementById('logo-upload').click()}
                  >
                    上传Logo
                  </Button>
                  <p className="text-xs text-[#8b949e] mt-1">支持图片, 不超过1MB, 保存后生效</p>
                </div>
              </div>
            </div>

            {/* 允许注册 */}
            <div>
              <label className="flex items-center justify-between text-[#57606a] mb-2">
                <span>允许新用户注册</span>
                <button
                  type="button"
                  onClick={() => setAllowRegistration(!allowRegistration)}
                  className="focus:outline-none"
                >
                  {allowRegistration ? (
                    <FiToggleRight className="text-[#0969da] text-2xl" />
                  ) : (
                    <FiToggleLeft className="text-[#8b949e] text-2xl" />
                  )}
                </button>
              </label>
              <p className="text-sm text-[#656d76]">
                {allowRegistration
                  ? '目前新用户可以自由注册账户'
                  : '目前禁止新用户注册，只有超级管理员可以创建账户'}
              </p>
            </div>
          </div>
        </motion.div>

        {/* 外观设置: 主题颜色 */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-md shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-[#1f2328] mb-6 flex items-center">
            <FiType className="mr-2" /> 外观设置
          </h2>

          <div className="space-y-8">
            {/* 主题颜色 */}
            <div>
              <label className="block text-[#57606a] mb-4">主题颜色</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#656d76] mb-2">主要颜色</p>
                  <ColorPicker
                    color={primaryColor}
                    onChange={setPrimaryColor}
                    presetColors={[
                      '#3B82F6', // 蓝色
                      '#10B981', // 绿色
                      '#8B5CF6', // 紫色
                      '#F59E0B', // 黄色
                      '#EF4444', // 红色
                      '#EC4899', // 粉色
                    ]}
                  />
                </div>
                <div>
                  <p className="text-sm text-[#656d76] mb-2">辅助颜色</p>
                  <ColorPicker
                    color={secondaryColor}
                    onChange={setSecondaryColor}
                    presetColors={[
                      '#4F46E5', // 靛蓝
                      '#059669', // 墨绿
                      '#6D28D9', // 深紫
                      '#D97706', // 深黄
                      '#DC2626', // 深红
                      '#BE185D', // 深粉
                    ]}
                  />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-[#656d76] mb-2">效果预览</p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    className="py-2 px-4 rounded-md text-[#1f2328]"
                    style={{ backgroundColor: primaryColor }}
                  >
                    主要按钮
                  </button>
                  <button
                    type="button"
                    className="py-2 px-4 rounded-md text-[#1f2328]"
                    style={{ backgroundColor: secondaryColor }}
                  >
                    辅助按钮
                  </button>
                </div>
              </div>
            </div>

            <div>
              <p className="text-[#656d76] text-sm">
                主题颜色设置会影响系统整体按钮、链接和强调色。修改后即时生效，但部分组件可能需要刷新页面才能完全应用新样式。
              </p>
            </div>
          </div>
        </motion.div>

        {/* 安全设置(仅超管) */}
        <motion.div
          variants={itemVariants}
          className="lg:col-span-2 bg-white rounded-md shadow-sm p-6"
        >
          <h2 className="text-lg font-semibold text-[#1f2328] mb-6 flex items-center">
            <FiShield className="mr-2" /> 安全设置
          </h2>

          <div className="space-y-6">
            <p className="text-[#656d76]">
              系统使用多重安全机制保护数据安全：
            </p>

            <ul className="list-disc pl-5 space-y-2 text-[#57606a]">
              <li>所有授权码使用AES-256-CBC加密存储</li>
              <li>授权码与特定应用绑定，无法跨应用使用</li>
              <li>用户密码使用bcrypt加盐哈希保护</li>
              <li>API接口使用JWT令牌认证</li>
              <li>请求速率限制防护暴力破解</li>
            </ul>

            <div className="bg-[#ddf4ff] p-4 rounded-md border border-[#9be9ff]">
              <p className="text-[#0969da] text-sm">
                注意：系统安全性取决于服务器环境和管理员配置。请确保服务器安全并定期更改管理员密码。
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* 保存按钮(平台级表单) */}
      <motion.div
        variants={itemVariants}
        className="mt-6 flex justify-end"
      >
        <Button
          type="button"
          variant="primary"
          icon={<FiSave />}
          disabled={updateMutation.isLoading}
          onClick={handleSubmit}
          style={{ backgroundColor: primaryColor }}
        >
          {updateMutation.isLoading ? '保存中...' : '保存设置'}
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default SystemSettings;
